import { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../lib/utils";
import { Tone, TONE_TEXT, TONE_TINT } from "./tones";

interface Props {
  label: ReactNode;
  value: ReactNode;
  /** Small muted line under the value ("63.83% Open Rate"). */
  hint?: ReactNode;
  icon?: LucideIcon;
  /** Tinted variant (Smartlead's analytics tiles). Omit for a plain white card. */
  tone?: Tone;
  className?: string;
  /** Makes the tile a button (the whole tile is the target), for tiles that open the thing they count. */
  onClick?: () => void;
}

/** Stat tile: label row with optional icon, big colored number, muted hint. */
export default function KpiCard({ label, value, hint, icon: Icon, tone, className, onClick }: Props) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      {...(onClick ? { type: "button" as const, onClick } : {})}
      className={cn(
        "rounded-xl border p-4 flex flex-col gap-2 min-w-0",
        tone ? TONE_TINT[tone] : "bg-surface border-border",
        onClick && "text-left transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        className,
      )}
    >
      <div className={cn("flex items-center gap-2 text-sm min-w-0", tone ? "text-foreground" : "text-muted-foreground")}>
        {Icon && <Icon className={cn("w-4 h-4 shrink-0", tone ? TONE_TEXT[tone] : "text-muted-foreground")} />}
        <span className="truncate">{label}</span>
      </div>
      <p className={cn("text-3xl font-semibold leading-none tracking-tight", tone ? TONE_TEXT[tone] : "text-foreground")}>
        {value}
      </p>
      {hint && <p className={cn("text-muted-foreground", tone ? "text-sm" : "text-xs")}>{hint}</p>}
    </Tag>
  );
}
